// src/pages/About.js
import React from "react";
import {
  FaHandshake,
  FaAward,
  FaLightbulb,
  FaShieldAlt,
  FaArrowRight,
  FaUsers,
  FaPhone,
  FaCode,
  FaChartLine
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import SEO from "../components/SEO";

const About = () => {
  const { t } = useTranslation();

  // Colors for consistent theming
  const primaryTextColor = "text-[#212529]"; // Dark text
  const secondaryTextColor = "text-[#212529]/80"; // Medium gray
  const tertiaryTextColor = "text-[#212529]/60"; // Light gray

  const [heroRef, heroInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [teamRef, teamInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-50px",
  });

  const [introRef, introInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-50px",
  });

  const [missionRef, missionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-50px",
  });

  const [visionRef, visionInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-50px",
  });

  const [ctaRef, ctaInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
    rootMargin: "-50px",
  });

  return (
    <div className="min-h-screen font-sans overflow-hidden relative">
      <SEO 
        title="About Axialis - Innovators in Electric Motor Technology"
        description="Meet the Axialis team of engineers and innovators dedicated to developing high-performance axial flux motors. Learn about our mission and vision."
        keywords="Axialis team, electric motor engineers, Mislav Cvitković, Marko Đula, Marin Žmire, axial flux motor development, electric propulsion innovation"
        path="/about"
      />
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-8"
      >
        <div className="container mx-auto px-4">
          <div
            className={`max-w-3xl mx-auto text-center relative z-20 transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${primaryTextColor}`}>
              About Us
            </h1>
            <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
            <p className={`text-lg md:text-xl ${secondaryTextColor} mb-10 leading-relaxed`}>
              At Axialis, we are a dedicated team of engineers and innovators driven by a shared passion for technology and precision.
            </p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section
        ref={teamRef}
        className="py-8"
      >
        <div className="container mx-auto px-4">
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto transition-all duration-1000 transform ${teamInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            {/* Team Member 1 */}
            <div className="text-center flex flex-col">
              <div className="backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 h-full flex flex-col">
                <div className="mb-4 overflow-hidden rounded-lg h-64">
                  <img
                    src="/images/team/mislavcvitkovic-500_orig.jpg"
                    alt="Dr. Mislav Cvitković"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className={`text-xl font-medium mb-1 ${primaryTextColor}`}>Dr. Mislav Cvitković</h3>
                <p className={`font-medium ${secondaryTextColor}`}>Owner</p>
                <p className={`${secondaryTextColor}`}>CEO</p>
              </div>
            </div>

            {/* Team Member 2 */}
            <div className="text-center flex flex-col">
              <div className="backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 h-full flex flex-col">
                <div className="mb-4 overflow-hidden rounded-lg h-64">
                  <img
                    src="/images/team/1606119591653.jpg"
                    alt="Marko Đula"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className={`text-xl font-medium mb-1 ${primaryTextColor}`}>Marko Đula (mag. ing. mech.)</h3>
                <p className={`font-medium ${secondaryTextColor}`}>Owner</p>
                <p className={`${secondaryTextColor}`}>CEO</p>
              </div>
            </div>

            {/* Team Member 3 */}
            <div className="text-center flex flex-col">
              <div className="backdrop-blur-sm bg-white/40 rounded-xl p-6 shadow-xl border border-white/40 h-full flex flex-col">
                <div className="mb-4 overflow-hidden rounded-lg h-64">
                  <img
                    src="/images/team/photo-2023-06-05-20-11-06.jpg"
                    alt="Marin Žmire"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className={`text-xl font-medium mb-1 ${primaryTextColor}`}>Marin Žmire (mag. ing. el.)</h3>
                <p className={`font-medium ${secondaryTextColor}`}>Owner</p>
                <p className={`${secondaryTextColor}`}>CEO</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <h2 className={`text-3xl font-medium mb-4 ${primaryTextColor}`}>
              Our Team
            </h2>
            <div className="h-0.5 w-16 bg-primary mx-auto"></div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section
        ref={introRef}
        className="py-16"
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center max-w-6xl mx-auto">
            <div
              className={`w-full transition-all duration-1000 transform ${introInView ? "translate-x-0 opacity-100" : "-translate-x-10 opacity-0"}`}
            >
              <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                <p className={`${secondaryTextColor} mb-6 leading-relaxed text-lg`}>
                  We believe that agility and expertise can empower even small teams to create impactful, forward-thinking solutions.
                </p>
                <p className={`${secondaryTextColor} mb-0 leading-relaxed text-lg`}>
                  Our axial flux permanent magnet (AFPM) motor has been developed through focused in-house engineering and extensive testing, reflecting our commitment to performance and efficiency.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section
        ref={missionRef}
        className="py-16"
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <h2 className={`text-3xl font-medium mb-4 ${primaryTextColor}`}>
              Mission
            </h2>
            <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
          </div>

          <div
            className={`max-w-4xl mx-auto transition-all duration-1000 transform ${missionInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
              <p className={`${secondaryTextColor} text-lg leading-relaxed text-center`}>
                To create high-performance, liquid-cooled electromechanical solutions tailored to the challenges of a rapidly changing world.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section
        ref={visionRef}
        className="py-16"
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className={`text-3xl font-medium mb-4 ${primaryTextColor}`}>
              Vision
            </h2>
            <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
          </div>

          <div
            className={`max-w-4xl mx-auto transition-all duration-1000 transform ${visionInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
              <p className={`${secondaryTextColor} text-lg leading-relaxed text-center`}>
                To deliver reliable and efficient motor solutions that support the evolution of modern electric propulsion systems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section
        ref={ctaRef}
        className="py-20"
      >
        <div className="container mx-auto px-4 text-center">
          <div
            className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
              <h3 className={`text-2xl md:text-3xl font-medium mb-4 ${primaryTextColor}`}>
                Ready to Learn More?
              </h3>
              <p className={`${secondaryTextColor} mb-8`}>
                Contact our team today to discover how our innovative motor solutions can meet your needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/contact"
                  className="bg-primary/90 hover:bg-primary text-white backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                >
                  Contact Us
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <Link
                  to="/explore-motor"
                  className="bg-white/70 hover:bg-white/80 text-[#212529] backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                >
                  Explore Our Motor
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;